/**
 * 日志管理服务
 */

import { httpClient } from './httpClient';
import { API_ENDPOINTS } from './api/config';
import { LogRecord, LogQueryParams, LogStatistics, LogDetail, LogResult, LogLevel } from '../types/log';

// 日志服务
export const logService = {
  // 获取日志列表
  async getList(params: LogQueryParams) {
    return httpClient.get(API_ENDPOINTS.LOG.LIST, { params });
  },

  // 获取日志详情
  async getDetail(id: number) {
    return httpClient.get(`${API_ENDPOINTS.LOG.DETAIL}/${id}`);
  },

  // 获取日志统计信息
  async getStatistics(params?: { startTime?: string; endTime?: string }) {
    return httpClient.get(API_ENDPOINTS.LOG.STATISTICS, { params });
  },

  // 导出日志
  async exportLogs(params: LogQueryParams) {
    return httpClient.get(API_ENDPOINTS.LOG.EXPORT, { 
      params,
      responseType: 'blob'
    });
  },

  // 删除单条日志
  async delete(id: number) {
    return httpClient.delete(`${API_ENDPOINTS.LOG.DELETE}/${id}`);
  },

  // 批量删除日志
  async batchDelete(ids: number[]) {
    return httpClient.delete(`${API_ENDPOINTS.LOG.DELETE}/batch`, { data: ids });
  },

  // 清理过期日志
  async cleanExpiredLogs(days: number) {
    return httpClient.delete(`${API_ENDPOINTS.LOG.CLEAN}?days=${days}`);
  },

  // 获取日志配置
  async getConfig() {
    return httpClient.get(API_ENDPOINTS.LOG.CONFIG);
  },

  // 更新日志配置
  async updateConfig(config: { enabled: boolean; actions: string[]; levels: string[] }) {
    return httpClient.put(API_ENDPOINTS.LOG.CONFIG, config);
  },
};

// 日志记录工具类
export class LogRecorder {
  private static instance: LogRecorder;
  private userId: number = 0;
  private userName: string = '';
  private userNickname: string = '';

  static getInstance(): LogRecorder {
    if (!LogRecorder.instance) {
      LogRecorder.instance = new LogRecorder();
    }
    return LogRecorder.instance;
  }

  // 设置当前用户信息
  setUserInfo(userId: number, userName: string, userNickname?: string) {
    this.userId = userId;
    this.userName = userName;
    this.userNickname = userNickname || '';
  }

  // 记录日志
  async record(logData: Partial<LogRecord>): Promise<void> {
    try {
      const logRecord: LogRecord = {
        userId: this.userId,
        userName: this.userName,
        userNickname: this.userNickname,
        module: '',
        action: logData.action!,
        targetType: '',
        description: '',
        result: LogResult.SUCCESS,
        level: LogLevel.INFO,
        ...logData,
      };

      // 异步记录日志，不阻塞主流程
      this.sendLogAsync(logRecord);
    } catch (error) {
      console.error('记录日志失败:', error);
    }
  }

  // 异步发送日志
  private async sendLogAsync(logRecord: LogRecord): Promise<void> {
    try {
      await httpClient.post(API_ENDPOINTS.LOG.CREATE, logRecord);
    } catch (error) {
      console.error('发送日志失败:', error);
    }
  }

  // 记录成功操作
  async recordSuccess(params: {
    module: string;
    action: LogRecord['action'];
    targetType: string;
    targetId?: number;
    targetTitle?: string;
    description: string;
    requestUrl?: string;
    requestMethod?: string;
    requestParams?: any;
    responseData?: any;
    executionTime?: number;
  }): Promise<void> {
    await this.record({
      ...params,
      result: LogResult.SUCCESS,
      level: LogLevel.INFO,
      requestParams: params.requestParams ? JSON.stringify(params.requestParams) : undefined,
      responseData: params.responseData ? JSON.stringify(params.responseData) : undefined,
    });
  }

  // 记录失败操作
  async recordError(params: {
    module: string;
    action: LogRecord['action'];
    targetType: string;
    targetId?: number;
    targetTitle?: string;
    description: string;
    errorMessage: string;
    requestUrl?: string;
    requestMethod?: string;
    requestParams?: any;
    executionTime?: number;
  }): Promise<void> {
    await this.record({
      ...params,
      result: LogResult.FAILED,
      level: LogLevel.ERROR,
      requestParams: params.requestParams ? JSON.stringify(params.requestParams) : undefined,
    });
  }

  // 记录数据变更（包含变更前后数据对比）
  async recordDataChange(params: {
    module: string;
    action: LogRecord['action'];
    targetType: string;
    targetId?: number;
    targetTitle?: string;
    description: string;
    beforeData?: any;
    afterData?: any;
    changes?: Array<{ field: string; oldValue: any; newValue: any }>;
    requestUrl?: string;
    requestMethod?: string;
    executionTime?: number;
  }): Promise<void> {
    await this.record({
      ...params,
      result: LogResult.SUCCESS,
      level: LogLevel.INFO,
      beforeData: params.beforeData ? JSON.stringify(params.beforeData) : undefined,
      afterData: params.afterData ? JSON.stringify(params.afterData) : undefined,
    });
  }
}

// 获取日志记录器实例
export const logRecorder = LogRecorder.getInstance();

export default { logService, logRecorder };
